import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ShoppingBag } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { NAV } from '@/constants/testIds';

const LOGO = 'https://customer-assets.emergentagent.com/job_cb412e5d-8af6-43ed-9f51-c9b9fc01bd76/artifacts/t1468n8c_SUMAYA_no%20BG_Green.png';

const links = [
  { id: 'shop', label: 'Shop', testId: NAV.linkShop },
  { id: 'goal', label: 'Shop by Goal', testId: NAV.linkGoal },
  { id: 'quiz', label: 'Tea Quiz', testId: NAV.linkQuiz },
  { id: 'ingredients', label: 'Ingredients', testId: NAV.linkIngredients },
  { id: 'reviews', label: 'Reviews', testId: NAV.linkReviews },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== '/') {
      navigate(`/#${id}`);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[#FAF4E3]/85 backdrop-blur-xl border-b border-[#E5DCC3]'
          : 'bg-transparent'
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <div className="flex items-center justify-between h-20 md:h-24">
          <Link
            to="/"
            data-testid={NAV.logo}
            className="flex items-center gap-2 group"
            aria-label="Sumaya home"
          >
            <img
              src={LOGO}
              alt="Sumaya"
              className="h-10 md:h-12 w-auto transition-transform duration-500 group-hover:scale-105"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={l.testId}
                onClick={() => go(l.id)}
                className="text-[15px] font-medium tracking-wide text-[#1D7448] link-sweep transition-colors hover:text-[#155A36]"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => go('shop')}
              data-testid={NAV.ctaShop}
              className="hidden md:inline-flex items-center gap-2 bg-[#1D7448] hover:bg-[#155A36] text-[#FAF4E3] px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 btn-shine"
            >
              <ShoppingBag className="w-4 h-4" />
              Shop
            </button>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid={NAV.mobileToggle}
              aria-label="Toggle menu"
              className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-[#1D7448]/25 text-[#1D7448]"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={NAV.mobileMenu}
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-[#FAF4E3]/95 backdrop-blur-xl border-t border-[#E5DCC3]"
          >
            <div className="mx-auto max-w-7xl px-6 py-6 flex flex-col gap-1">
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="text-left font-serif text-2xl text-[#1D7448] py-3 border-b border-[#E5DCC3] last:border-b-0"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
